import React from "react";
import { Progress } from "../../components/ui/progress";
import { Button } from "../../components/ui/button";
import { ProductQualityMetric } from "../../types/api";
import { Eye, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { generateAllMessages } from "./productStates";
import { RootState } from "../../store/store";
import { getProductScore } from "../../store/score/getProductScore";
import ScoreBreakdownTable from "./ScoreBreakdownTable";
import { getScoreColor } from "./utils";
import ProductImage from "./ProductImage";
import { setEnrichmentDialogOpen } from "../../store/flowSlice";
import { Check, AlertCircle } from "lucide-react";
import { Collapse } from "@mui/material";
import { useDebugMode } from "../../shared/hooks";
import { useTranslation } from "../../i18n/hooks";


interface ProductRowProps {
  product: ProductQualityMetric;
}

const ProductRow = ({
  product,
}: ProductRowProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const { contentScore: score, issues } = useSelector((state: RootState) =>
    getProductScore(state.score, product.product_id, "base")
  );
  const { t } = useTranslation();

  const handleToggle = () : void => {
    setIsOpen(!isOpen);
  };

  const isDebugMode = useDebugMode();

  const messages = generateAllMessages([
    { label: "total_title_score", score: product.total_title_score },
    { label: "total_desc_html_score", score: product.total_desc_html_score },
    { label: "images_count", score: product.images_objects.length },
    {
      label: "total_seo_description_score",
      score: product.total_seo_description_score,
    },
    {
      label: "total_category_placement_score",
      score: product.total_category_placement_score ?? 0,
    },
    { label: "total_alt_text_score", score: product.total_alt_text_score ?? 0 },
    { label: "total_tags_score", score: product.total_tags_score },
  ]);

  const scoreColors = getScoreColor(score);

  return (
    <div>
      <div
        onClick={handleToggle}
        className="grid grid-cols-[2fr,120px] md:grid-cols-[2.5fr,120px,120px,100px,40px] lg:grid-cols-[3fr,140px,140px,120px,40px] gap-2 md:gap-4 items-center"
      >
        {/* Product Info */}
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <ProductImage product={product} />
          <div className="min-w-0">
            {" "}
            {/* Parent container */}
            <div className="flex items-center gap-2 relative pr-8 group">
              {" "}
              {/* Added group class */}
              <div className="text-sm text-gray-600 line-clamp-2 leading-snug">
                {product.title}
              </div>
              {product.product_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1 hover:bg-gray-100 absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(product.product_url, "_blank");
                  }}
                >
                  <Eye className="w-4 h-4 text-gray-400" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content Score */}
        <div className="flex items-center gap-2">
          <Progress
            value={score}
            className={`w-16 md:w-20 h-2 [&>div]:${scoreColors.progress}`}
          />
          <span className={`text-sm ${scoreColors.text}`}>{score}%</span>
        </div>

        {/* Issue Tags */}
        <div className="hidden md:flex flex-wrap justify-center gap-1.5 mt-1 mb-2">
          {issues.map((issue, index) => (
            <span
              key={index}
              className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs"
            >
              {issue}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="hidden md:flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:bg-blue-50"
            onClick={(e) => {
              e.stopPropagation();
              dispatch(
                setEnrichmentDialogOpen({
                  isOpen: true,
                  enrichedProducts: [product.product_id],
                  selectedFeatures: [
                    "title",
                    "description",
                    "seo",
                    "altText",
                    "productTags",
                  ],
                })
              );
            }}
          >
            {t('productsView.enhance')}
          </Button>

        </div>
        <button
            onClick={handleToggle}
            className="hidden justify-self-center w-6 h-6 md:flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
      </div>

      {/* Detail View */}
      <Collapse in={isOpen}>
        <div className="border-t bg-gray-50">
          <div className="py-4 px-6 ml-2  md:ml-[48px]">
            <div className="space-y-2">
              {messages.map((message, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  {message?.isSuccess ? (
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  )}
                  <span>{message?.message}</span>
                </div>
              ))}
            </div>

            {isDebugMode &&
              product.total_title_score_components &&
              product.total_desc_html_score_components &&
              product.total_tags_score_components &&
              product.total_seo_description_score_components && (
                <ScoreBreakdownTable
                  titleScores={product.total_title_score_components}
                  descriptionScores={product.total_desc_html_score_components}
                  tagScores={product.total_tags_score_components}
                  seoScores={product.total_seo_description_score_components}
                />
              )}
          </div>
        </div>
      </Collapse>
    </div>
  );
};
export default ProductRow;
